import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Phone,
  Send,
  MoreVertical,
  Users,
  ShieldCheck,
  Clock,
  Lock,
  Eye,
  Keyboard,
  Download,
  Pencil,
  Trash2,
  X,
  Check,
  Fingerprint,
  Atom,
} from 'lucide-react';
import { SecureMessage } from '../SecureMessage';
import { VirtualKeyboardTrigger } from '../VirtualKeyboard';
import { FileUpload, FileMessage } from '../FileSharing';
import { MessageReactions, ReactionPicker } from '../MessageReactions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Info, EyeOff, Upload, Archive, AlertTriangle, CheckCircle2, Search, ChevronUp, ChevronDown, Zap, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { setItem as lsSet, removeItem as lsRemove } from '../../utils/storage';
import {
  loadImportedHistory,
  saveImportedHistory,
  clearImportedHistory,
  loadImportedEnvelope,
} from '../../utils/conversationHistory';
import { computeMessagesHash } from '../../utils/integrity';
import { SafetyNumberDialog } from '../SafetyNumberDialog';
import { KeyTransparencyDialog } from '../KeyTransparencyDialog';
import { SafetyStripe } from '../SafetyStripe';
import { PqTourCard } from '../PqTourCard';
import { RotationNudgePrompt } from '../RotationNudgePrompt';
import { ConversationTTLPicker } from '../ConversationTTLPicker';

const HIDE_LEGACY_KEY = 'ghostcomm_hide_undecryptable';
const LEGACY_SENTINELS = new Set([
  '[Encrypted for a different device]',
  '[Decryption failed]',
]);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatView = ({ conversation, contacts, onCall, onGroupCall, isWindowFocused, onOpenVirtualKeyboard }) => {
  const { user, encryptMessage, decryptMessage, sendWsMessage, addWsListener, cryptoKeys, isDuressMode, duressState, setDuressState } = useAuth();
  const [messages, setMessages] = useState([]);
  // Mirror of `messages` in a ref so the WS listener effect below doesn't
  // need to resubscribe every time a new message arrives — that
  // resubscribe-on-every-update previously created a window where
  // `view_once_consumed` events could be silently dropped, leaving the
  // sender's UI stuck on the spent message until a full refresh.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [newMessage, setNewMessage] = useState('');
  const [decryptedMessages, setDecryptedMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [openReactionPicker, setOpenReactionPicker] = useState(null);
  const [pickerOpenedByKeyboard, setPickerOpenedByKeyboard] = useState(false);
  const [reactingMessageIds, setReactingMessageIds] = useState({}); // { [msgId]: timeoutId }
  const [focusedMessageId, setFocusedMessageId] = useState(null);
  // Edit-in-place: `editingMessageId` enables an inline textarea; `editDraft` is
  // the current edit buffer. Saving re-encrypts and PATCHes the server.
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // ─── Search state ─────────────────────────────────────────────────
  // `searchOpen` toggles the header search bar; `searchQuery` is the live
  // substring. Matches are derived from `decryptedMessages` (client-side only
  // — the server never sees plaintext). `currentMatchIndex` drives
  // prev/next navigation and auto-scroll.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const searchInputRef = useRef(null);

  // ─── View-once toggle ─────────────────────────────────────────────
  // When enabled, the next message sent is flagged `view_once: true` so the
  // server auto-clears its ciphertext on the first non-sender GET. The flag
  // resets to `false` after each send so a user can't leave "burn mode" on
  // accidentally for a whole conversation.
  const [viewOnceArmed, setViewOnceArmed] = useState(false);

  // ─── Per-conversation TTL ─────────────────────────────────────────
  // Sourced from the `conversation` prop; updated locally on
  // `ttl_updated` WS events so other participants' changes appear in
  // real time without a full re-fetch. The backend route is
  // PUT /api/conversations/{id}/ttl. Note: TTL changes only apply to
  // messages sent AFTER the change — see ConversationTTLPicker comment.
  const [ttlHours, setTtlHours] = useState(
    Number(conversation?.ttl_hours ?? 24),
  );
  useEffect(() => {
    setTtlHours(Number(conversation?.ttl_hours ?? 24));
  }, [conversation?.id, conversation?.ttl_hours]);

  // Per-message reveal state for the "hover to reveal" privacy mode.
  // Structure: `{ [messageId]: boolean }` — true once the user has
  // positively revealed that bubble; resets on cursor-leave so the next
  // person looking over your shoulder doesn't get it for free. Global
  // toggle lives in localStorage under `gs_hover_reveal`.
  const [hoverReveal, setHoverReveal] = useState(() => {
    try { return window.localStorage.getItem('gs_hover_reveal') === '1'; } catch { return false; }
  });
  const [revealedIds, setRevealedIds] = useState(() => new Set());

  // ─── Virtual-keyboard text injection ─────────────────────────────
  // The on-screen anti-keylogger keyboard dispatches a window-level
  // CustomEvent when the user commits text. We append it to our
  // message-compose buffer so the "Send" button is already armed with
  // the secure input — no retyping, no funneling into search.
  useEffect(() => {
    const onKeyboardInput = (e) => {
      const text = e?.detail;
      if (typeof text !== 'string' || !text) return;
      setNewMessage((current) => (current ? `${current} ${text}` : text));
      // Ack so Dashboard's fallback toast knows the chat caught it.
      window.dispatchEvent(new CustomEvent('gs:virtual-keyboard-ack'));
    };
    window.addEventListener('gs:virtual-keyboard-input', onKeyboardInput);
    return () => window.removeEventListener('gs:virtual-keyboard-input', onKeyboardInput);
  }, []);

  // ─── External compose prefill ────────────────────────────────────
  // Other surfaces (e.g. the "Start chat" flow from an invite
  // redemption row with a suggested welcome message) can dispatch
  // `gs:compose-prefill` with `{ conversationId, text }` to pre-fill
  // this chat's compose buffer. We deliberately REPLACE the buffer
  // (vs append) so the draft arrives intact; the user still has to
  // hit Send, so nothing is auto-transmitted without a last-look.
  useEffect(() => {
    const onPrefill = (e) => {
      const detail = e?.detail;
      if (!detail || typeof detail.text !== 'string') return;
      if (detail.conversationId && detail.conversationId !== conversation.id) return;
      setNewMessage(detail.text);
    };
    window.addEventListener('gs:compose-prefill', onPrefill);
    return () => window.removeEventListener('gs:compose-prefill', onPrefill);
  }, [conversation.id]);

  // Safety-number dialog (MITM verification).
  const [safetyNumberOpen, setSafetyNumberOpen] = useState(false);
  const [keyHistoryOpen, setKeyHistoryOpen] = useState(false);
  const [hideLegacy, setHideLegacy] = useState(() => {
    try { return window.localStorage.getItem(HIDE_LEGACY_KEY) === '1'; } catch { return false; }
  });

  // Import-history dialog state.
  const [importOpen, setImportOpen] = useState(false);
  const [importParsed, setImportParsed] = useState(null);
  const [importError, setImportError] = useState(null);
  const importFileRef = useRef(null);

  // The count of imported messages currently merged into the feed. Shown as a
  // small banner so the user knows part of the timeline came from a backup file.
  const importedEnvelope = loadImportedEnvelope(conversation.id);
  const importedCount = importedEnvelope?.messages?.length || 0;

  const resetImportState = useCallback(() => {
    setImportParsed(null);
    setImportError(null);
    if (importFileRef.current) importFileRef.current.value = '';
  }, []);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // ─── Sealed-sender lookup (Week 2) ───────────────────────────────
  // Server no longer persists `sender_id` on new messages — it
  // stores `sender_token = HMAC_SHA256(conv.sender_secret, user_id)`.
  // We fetch the conversation's sender-secret once, precompute an
  // HMAC for every participant, and build a {token → user_id} map so
  // we can attribute messages locally. Legacy messages (pre-sealed-
  // sender) still carry `sender_id` and are handled by the
  // `resolveSenderId` fallback below.
  const [tokenMap, setTokenMap] = useState({});
  // `peerHasPq` flips true once we've confirmed every NON-SELF
  // participant has a Kyber bundle on file. Used to drive the
  // "Quantum-safe" indicator in the header — we only show it when
  // ALL peers can actually receive hybrid envelopes, otherwise the
  // label would be misleading in mixed-mode groups.
  const [peerHasPq, setPeerHasPq] = useState(false);
  useEffect(() => {
    // Duress: never probe sender-secret — the decoy vault carries
    // `sender_id` inline, so the message renderer needs no HMAC map.
    if (isDuressMode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/conversations/${conversation.id}/sender-secret`
        );
        const secretB64 = res.data?.sender_secret;
        if (!secretB64) return;
        const rawKey = Uint8Array.from(atob(secretB64), (c) => c.charCodeAt(0));
        const cryptoKey = await window.crypto.subtle.importKey(
          'raw', rawKey, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
        );
        const participants = Array.from(
          new Set(
            [user.id, ...(conversation.participants || []),
             ...contacts.map((c) => c.id)]
          )
        );
        const map = {};
        for (const pid of participants) {
          if (!pid) continue;
          const macBuf = await window.crypto.subtle.sign(
            'HMAC', cryptoKey, new TextEncoder().encode(pid)
          );
          const hex = Array.from(new Uint8Array(macBuf))
            .map((b) => b.toString(16).padStart(2, '0')).join('');
          map[hex] = pid;
        }
        if (!cancelled) setTokenMap(map);
      } catch (_e) {
        // Pre-sealed conversation or fetch blocked — fall back to
        // whatever `sender_id` the legacy rows still carry.
      }
    })();
    return () => { cancelled = true; };
  }, [conversation.id, conversation.participants, contacts, user.id, isDuressMode]);

  // Normalize a message so every downstream consumer can safely read
  // `msg.sender_id` without caring about the on-disk representation.
  const resolveSenderId = useCallback((msg) => {
    if (!msg) return undefined;
    if (msg.sender_id) return msg.sender_id;
    if (msg.sender_token && tokenMap[msg.sender_token]) {
      return tokenMap[msg.sender_token];
    }
    return undefined;
  }, [tokenMap]);

  // Derived view: filter out undecryptable messages when the toggle is on.
  const legacyCount = messages.filter(
    (m) => !m.is_file && LEGACY_SENTINELS.has(decryptedMessages[m.id])
  ).length;
  const visibleMessages = hideLegacy
    ? messages.filter((m) => m.is_file || !LEGACY_SENTINELS.has(decryptedMessages[m.id]))
    : messages;

  const toggleHideLegacy = () => {
    setHideLegacy((v) => {
      const next = !v;
      try {
        if (next) lsSet(HIDE_LEGACY_KEY, '1');
        else lsRemove(HIDE_LEGACY_KEY);
      } catch {}
      return next;
    });
  };

  const getOtherParticipant = useCallback(() => {
    if (conversation.type === 'group') return null;
    const otherId = conversation.participants.find((p) => p !== user.id);
    return contacts.find((c) => c.id === otherId);
  }, [conversation, user, contacts]);

  // Fetch messages (and merge in any locally-imported history).
  // Imported messages are rendered inline, deduped by `id`, marked with
  // `_imported: true` so the UI can badge them as restored-from-backup.
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      // Duress: pull the scripted conversation from the decoy vault.
      if (isDuressMode && duressState) {
        const decoyMsgs = (duressState.messages || [])
          .filter((m) => m.conversation_id === conversation.id)
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(decoyMsgs);
        // Pre-populate the decrypted-plaintext cache directly from
        // `_plain` so the render loop never attempts real crypto.
        const cache = {};
        for (const m of decoyMsgs) cache[m.id] = m._plain || '';
        setDecryptedMessages(cache);
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/messages/${conversation.id}`);
        const imported = loadImportedHistory(conversation.id);
        if (imported.length) {
          const serverIds = new Set(res.data.map((m) => m.id));
          const onlyImported = imported.filter((m) => !serverIds.has(m.id));
          const merged = [...onlyImported, ...res.data].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
          setMessages(merged);
        } else {
          setMessages(res.data);
        }
      } catch (error) {
        toast.error('Failed to load messages');
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [conversation.id]);

  // Decrypt messages (both live and imported). Imported messages stored in
  // localStorage use `encrypted_keys[user.id]` — the import flow rewrites
  // exported `encrypted_key_for_exporter` into that shape before saving, so
  // this loop stays a single code path.
  useEffect(() => {
    const decrypt = async () => {
      const decrypted = {};
      for (const msg of messages) {
        const key = msg.encrypted_keys?.[user.id];
        const pqEnv = msg.encrypted_keys_pq?.[user.id];
        decrypted[msg.id] = await decryptMessage(msg.encrypted_content, key, pqEnv);
      }
      setDecryptedMessages(decrypted);
    };
    if (messages.length > 0) {
      decrypt();
    }
  }, [messages, user.id, decryptMessage]);

  // WebSocket listener
  useEffect(() => {
    const unsubscribe = addWsListener(async (data) => {
      if (data.type === 'new_message' && data.message.conversation_id === conversation.id) {
        setMessages((prev) => [...prev, data.message]);
        const key = data.message.encrypted_keys[user.id];
        const pqEnv = data.message.encrypted_keys_pq?.[user.id];
        const decrypted = await decryptMessage(data.message.encrypted_content, key, pqEnv);
        setDecryptedMessages((prev) => ({ ...prev, [data.message.id]: decrypted }));
      }
      if (data.type === 'typing' && data.conversation_id === conversation.id) {
        setTypingUsers((prev) => {
          if (!prev.includes(data.user_id)) return [...prev, data.user_id];
          return prev;
        });
        setTimeout(() => {
          setTypingUsers((prev) => prev.filter((id) => id !== data.user_id));
        }, 3000);
      }
      // Handle reaction updates
      if ((data.type === 'reaction_added' || data.type === 'reaction_removed') &&
          messagesRef.current.some((m) => m.id === data.message_id)) {
        setMessages((prev) => prev.map((msg) =>
          msg.id === data.message_id
            ? { ...msg, reactions: data.reactions }
            : msg
        ));
      }
      // Someone else opened a reaction picker on a message -> pulse it
      if (data.type === 'reaction_typing' &&
          data.conversation_id === conversation.id &&
          data.user_id !== user.id &&
          data.message_id) {
        const mid = data.message_id;
        setReactingMessageIds((prev) => {
          if (prev[mid]) clearTimeout(prev[mid]);
          const t = setTimeout(() => {
            setReactingMessageIds((p) => {
              const { [mid]: _drop, ...rest } = p;
              return rest;
            });
          }, 2500);
          return { ...prev, [mid]: t };
        });
      }
      // Message edited on another device / by another participant — swap in
      // the new ciphertext + decrypt with this device's wrapped key.
      if (data.type === 'message_edited' && data.message?.conversation_id === conversation.id) {
        const updated = data.message;
        setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
        const key = updated.encrypted_keys?.[user.id];
        const pqEnv = updated.encrypted_keys_pq?.[user.id];
        const decrypted = await decryptMessage(updated.encrypted_content, key, pqEnv);
        setDecryptedMessages((prev) => ({ ...prev, [updated.id]: decrypted }));
      }
      // Message deleted — replace with tombstone so timeline/reactions don't
      // jump around. Drop any cached plaintext we had for it.
      if (data.type === 'message_deleted' && data.conversation_id === conversation.id) {
        setMessages((prev) => prev.map((m) =>
          m.id === data.message_id
            ? { ...m, deleted: true, encrypted_content: '', encrypted_keys: {}, reactions: {} }
            : m
        ));
        setDecryptedMessages((prev) => {
          const { [data.message_id]: _drop, ...rest } = prev;
          return rest;
        });
      }
      // A view-once message was just consumed by its recipient — clear the
      // payload locally so the sender (and any other device) sees the spent
      // state. We DON'T drop the decrypted plaintext cache here because the
      // sender is allowed to keep seeing what they sent.
      if (data.type === 'view_once_consumed' && data.conversation_id === conversation.id) {
        setMessages((prev) => prev.map((m) =>
          m.id === data.message_id
            ? {
                ...m,
                view_once_consumed: true,
                // Keep `encrypted_content` only for the sender; recipient
                // keys mapping is useless post-consumption.
                encrypted_keys: resolveSenderId(m) === user.id ? m.encrypted_keys : {},
                encrypted_content: resolveSenderId(m) === user.id ? m.encrypted_content : '',
              }
            : m
        ));
        if (data.consumed_by !== user.id) {
          // Recipient consumed their OWN copy via another device — nuke the
          // cached plaintext everywhere else too.
          setDecryptedMessages((prev) => {
            const { [data.message_id]: _drop, ...rest } = prev;
            return rest;
          });
        }
      }
      // A participant changed the conversation's auto-destruct TTL.
      // Update the local picker label in real time (functional setter
      // — see React-18 batching note in handoff summary).
      if (data.type === 'ttl_updated' && data.conversation_id === conversation.id) {
        setTtlHours(Number(data.ttl_hours ?? 24));
      }
      // Group admin removed someone from the room. We inject a small
      // ephemeral system message so the social signal isn't a silent
      // shrink of the participant list. The synthetic message is
      // local-only (`_system: true`) — never persisted, never sent.
      if (data.type === 'participant_removed' && data.conversation_id === conversation.id) {
        const removedUser = contacts.find((c) => c.id === data.user_id);
        const remover = contacts.find((c) => c.id === data.removed_by);
        const removedName = data.user_id === user.id
          ? 'You'
          : (removedUser?.username || 'A member');
        const removerName = data.removed_by === user.id
          ? 'you'
          : (remover?.username || 'an admin');
        setMessages((prev) => [
          ...prev,
          {
            id: `__system_pr_${data.user_id}_${Date.now()}`,
            _system: true,
            conversation_id: conversation.id,
            created_at: new Date().toISOString(),
            text: `${removedName} ${data.user_id === user.id ? 'were' : 'was'} removed by ${removerName}.`,
          },
        ]);
      }
    });
    return unsubscribe;
  }, [addWsListener, conversation.id, user.id, decryptMessage, contacts]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, decryptedMessages]);

  // Keyboard shortcut: press "R" while a message bubble is focused to open its reaction picker.
  // Ignored when typing in any input/textarea/contenteditable.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (!focusedMessageId) return;
      e.preventDefault();
      setPickerOpenedByKeyboard(true);
      setOpenReactionPicker(focusedMessageId);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedMessageId]);

  // Get participant public keys
  const getParticipantPublicKeys = useCallback(async () => {
    const keys = {};
    const pqBundles = {};
    // Duress: decoy contacts have no real public keys and must not
    // generate 404 traffic that reveals the fake IDs to the server.
    if (isDuressMode) return { keys, pqBundles };
    for (const participantId of conversation.participants) {
      if (participantId === user.id) continue;
      try {
        const res = await axios.get(`${API}/users/${participantId}`);
        if (res.data.public_key) {
          keys[participantId] = res.data.public_key;
        }
        // Also fetch the PQ bundle (404-tolerant). If the peer has a
        // Kyber pubkey we'll produce a hybrid envelope for them.
        try {
          const bundleRes = await axios.get(`${API}/users/${participantId}/pq-bundle`);
          if (bundleRes.data?.kyber_pub) {
            pqBundles[participantId] = {
              kyber_pub_b64: bundleRes.data.kyber_pub,
              dilithium_pub_b64: bundleRes.data.dilithium_pub,
              bundle_sig_b64: bundleRes.data.bundle_sig,
              bundle_created_at: bundleRes.data.bundle_created_at,
              rsa_pub: bundleRes.data.rsa_pub,
            };
          }
        } catch (_e) { /* no PQ bundle → classical-only for this peer */ }
      } catch (e) {}
    }
    return { keys, pqBundles };
  }, [conversation.participants, user.id, isDuressMode]);

  // Probe the PQ-readiness of every non-self participant once per
  // conversation so the header indicator is accurate. Cheap — one
  // fetch per peer, 404-tolerant.
  useEffect(() => {
    // Duress: pretend peers are NOT PQ-ready and skip the network
    // reveal. The decoy contacts don't exist on the server.
    if (isDuressMode) { setPeerHasPq(false); return; }
    let cancelled = false;
    (async () => {
      const others = (conversation.participants || []).filter((p) => p !== user.id);
      if (others.length === 0) { setPeerHasPq(false); return; }
      let allHavePq = true;
      for (const pid of others) {
        try {
          const r = await axios.get(`${API}/users/${pid}/pq-bundle`);
          if (!r.data?.kyber_pub) { allHavePq = false; break; }
        } catch (_e) { allHavePq = false; break; }
      }
      if (!cancelled) setPeerHasPq(allHavePq);
    })();
    return () => { cancelled = true; };
  }, [conversation.id, conversation.participants, user.id, isDuressMode]);

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    // Duress: construct a local message row that matches the decoy
    // vault shape, push it into both local UI state AND the shared
    // duressState so switching conversations preserves the history
    // within this session. No network activity — a packet sniffer
    // watching the coerced unlock sees nothing.
    if (isDuressMode) {
      setSending(true);
      try {
        const now = Date.now();
        const fakeMsg = {
          id: `decoy-m-sent-${now}-${Math.floor(Math.random() * 1e6).toString(36)}`,
          conversation_id: conversation.id,
          sender_id: user.id,
          _plain: newMessage,
          encrypted_content: null,
          encrypted_keys: {},
          created_at: new Date(now).toISOString(),
          expires_at: new Date(now + 86400_000).toISOString(),
          is_read: true,
          reactions: {},
          edited: false,
          deleted: false,
        };
        setMessages((prev) => [...prev, fakeMsg]);
        setDecryptedMessages((prev) => ({ ...prev, [fakeMsg.id]: newMessage }));
        setDuressState((prev) => prev ? {
          ...prev,
          messages: [...(prev.messages || []), fakeMsg],
          conversations: (prev.conversations || []).map((c) =>
            c.id === conversation.id
              ? { ...c, last_message_at: fakeMsg.created_at }
              : c
          ),
        } : prev);

        if (viewOnceArmed) setViewOnceArmed(false);
        setNewMessage('');

        // Schedule a plausible auto-reply from the decoy peer so
        // the coercer sees a live-feeling conversation. Direct
        // (1:1) only — auto-replying as "everyone" in a decoy
        // group would read as robotic.
        if (conversation.type !== 'group') {
          const peerId = (conversation.participants || []).find((p) => p !== user.id);
          if (peerId) {
            const { scheduleAutoReply } = await import('../../utils/duressVault');
            scheduleAutoReply((replyText) => {
              const ts = Date.now();
              const inbound = {
                id: `decoy-m-reply-${ts}-${Math.floor(Math.random() * 1e6).toString(36)}`,
                conversation_id: conversation.id,
                sender_id: peerId,
                _plain: replyText,
                encrypted_content: null,
                encrypted_keys: {},
                created_at: new Date(ts).toISOString(),
                expires_at: new Date(ts + 86400_000).toISOString(),
                is_read: false,
                reactions: {},
                edited: false,
                deleted: false,
              };
              setMessages((prev) => [...prev, inbound]);
              setDecryptedMessages((prev) => ({ ...prev, [inbound.id]: replyText }));
              setDuressState((prev) => prev ? {
                ...prev,
                messages: [...(prev.messages || []), inbound],
                conversations: (prev.conversations || []).map((c) =>
                  c.id === conversation.id
                    ? { ...c, last_message_at: inbound.created_at }
                    : c
                ),
              } : prev);
            });
          }
        }
      } finally {
        setSending(false);
      }
      return;
    }

    setSending(true);
    try {
      const { keys: recipientKeys, pqBundles } = await getParticipantPublicKeys();
      const { encrypted_content, encrypted_keys, encrypted_keys_pq } = await encryptMessage(
        newMessage, recipientKeys, pqBundles
      );

      const res = await axios.post(`${API}/messages`, {
        conversation_id: conversation.id,
        encrypted_content,
        encrypted_keys,
        encrypted_keys_pq,
        view_once: viewOnceArmed,
      });

      setMessages((prev) => [...prev, res.data]);
      setDecryptedMessages((prev) => ({ ...prev, [res.data.id]: newMessage }));

      sendWsMessage({
        type: 'message',
        conversation_id: conversation.id,
        message: res.data,
      });

      // Reset view-once after send so it's always an opt-in per-message action.
      if (viewOnceArmed) setViewOnceArmed(false);

      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle typing
  const handleTyping = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    sendWsMessage({
      type: 'typing',
      conversation_id: conversation.id,
    });
    typingTimeoutRef.current = setTimeout(() => {}, 3000);
  };

  const otherParticipant = getOtherParticipant();
  const conversationName =
    conversation.type === 'group'
      ? conversation.encrypted_name || 'Group Chat'
      : otherParticipant?.username || 'Unknown';

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // ─── Message edit / delete (15-min window, sender-only enforced by API) ──
  const EDIT_WINDOW_MS = 15 * 60 * 1000;

  const canEditMessage = (msg) => {
    if (!msg || resolveSenderId(msg) !== user.id) return false;
    if (msg.is_file || msg.deleted) return false;
    try {
      return Date.now() - new Date(msg.created_at).getTime() < EDIT_WINDOW_MS;
    } catch {
      return false;
    }
  };

  const canDeleteMessage = (msg) => {
    return !!msg && resolveSenderId(msg) === user.id && !msg.deleted;
  };

  const beginEdit = (msg) => {
    const current = decryptedMessages[msg.id];
    // If we can't decrypt the current ciphertext there's nothing safe to edit.
    if (!current || LEGACY_SENTINELS.has(current)) {
      toast.error('Cannot edit this message on this device');
      return;
    }
    setEditingMessageId(msg.id);
    setEditDraft(current);
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditDraft('');
  };

  const saveEdit = async (msg) => {
    if (!editDraft.trim() || savingEdit) return;
    // Duress: mutate local vault state only. No network call.
    if (isDuressMode) {
      setSavingEdit(true);
      try {
        const editedAt = new Date().toISOString();
        setMessages((prev) => prev.map((m) =>
          m.id === msg.id ? { ...m, _plain: editDraft, edited: true, edited_at: editedAt } : m
        ));
        setDecryptedMessages((prev) => ({ ...prev, [msg.id]: editDraft }));
        setDuressState((prev) => prev ? {
          ...prev,
          messages: (prev.messages || []).map((m) =>
            m.id === msg.id ? { ...m, _plain: editDraft, edited: true, edited_at: editedAt } : m
          ),
        } : prev);
        toast.success('Message edited');
        cancelEdit();
      } finally {
        setSavingEdit(false);
      }
      return;
    }
    setSavingEdit(true);
    try {
      const { keys: recipientKeys, pqBundles } = await getParticipantPublicKeys();
      const { encrypted_content, encrypted_keys, encrypted_keys_pq } = await encryptMessage(
        editDraft, recipientKeys, pqBundles
      );
      const res = await axios.patch(`${API}/messages/${msg.id}`, {
        encrypted_content,
        encrypted_keys,
        encrypted_keys_pq,
      });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? res.data : m)));
      setDecryptedMessages((prev) => ({ ...prev, [msg.id]: editDraft }));
      toast.success('Message edited');
      cancelEdit();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Edit failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteMessage = async (msg) => {
    // Duress: flip the row to deleted locally — no DELETE traffic.
    if (isDuressMode) {
      setMessages((prev) => prev.map((m) =>
        m.id === msg.id
          ? { ...m, deleted: true, _plain: '', encrypted_content: '', encrypted_keys: {}, reactions: {} }
          : m
      ));
      setDecryptedMessages((prev) => {
        const { [msg.id]: _drop, ...rest } = prev;
        return rest;
      });
      setDuressState((prev) => prev ? {
        ...prev,
        messages: (prev.messages || []).map((m) =>
          m.id === msg.id
            ? { ...m, deleted: true, _plain: '', encrypted_content: '', encrypted_keys: {}, reactions: {} }
            : m
        ),
      } : prev);
      toast.success('Message deleted');
      return;
    }
    try {
      await axios.delete(`${API}/messages/${msg.id}`);
      setMessages((prev) => prev.map((m) =>
        m.id === msg.id
          ? { ...m, deleted: true, encrypted_content: '', encrypted_keys: {}, reactions: {} }
          : m
      ));
      setDecryptedMessages((prev) => {
        const { [msg.id]: _drop, ...rest } = prev;
        return rest;
      });
      toast.success('Message deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Compute search matches — messages whose decrypted text (or file name)
  // contains the query substring. Returns full message objects + a `matchText`
  // field so bubble rendering can highlight inline without re-searching.
  const searchMatches = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const hits = [];
    for (const msg of messages) {
      if (msg.deleted) continue;
      if (msg.is_file) {
        try {
          const meta = JSON.parse(msg.encrypted_content || '{}');
          const name = (meta.name || '').toLowerCase();
          if (name.includes(q)) hits.push(msg);
        } catch {}
        continue;
      }
      const text = decryptedMessages[msg.id];
      if (text && !LEGACY_SENTINELS.has(text) && text.toLowerCase().includes(q)) {
        hits.push(msg);
      }
    }
    return hits;
  }, [searchQuery, messages, decryptedMessages]);

  // Reset the cursor whenever the query changes or match count shifts.
  useEffect(() => {
    if (currentMatchIndex >= searchMatches.length) {
      setCurrentMatchIndex(Math.max(0, searchMatches.length - 1));
    }
  }, [searchMatches.length, currentMatchIndex]);

  // Auto-scroll the active match into view whenever the cursor moves.
  useEffect(() => {
    if (!searchOpen || searchMatches.length === 0) return;
    const target = searchMatches[currentMatchIndex];
    if (!target) return;
    const el = document.querySelector(`[data-testid="message-bubble-${target.id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentMatchIndex, searchOpen, searchMatches]);

  const nextMatch = useCallback(() => {
    if (!searchMatches.length) return;
    setCurrentMatchIndex((i) => (i + 1) % searchMatches.length);
  }, [searchMatches.length]);

  const prevMatch = useCallback(() => {
    if (!searchMatches.length) return;
    setCurrentMatchIndex((i) => (i - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches.length]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setCurrentMatchIndex(0);
  }, []);

  // Global keyboard: "/" opens, Esc closes. Ignore when another input is focused
  // so typing slash inside the message composer doesn't hijack the UX.
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target?.tagName;
      const isTyping =
        tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
      if (e.key === '/' && !isTyping && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && searchOpen) {
        closeSearch();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [searchOpen, closeSearch]);

  const highlightedText = (text) => {
    const q = searchQuery.trim();
    if (!q) return text;
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark
          key={i}
          className="bg-amber-400/40 text-amber-100 rounded-sm px-0.5"
        >
          {p}
        </mark>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  // Export the conversation as a still-encrypted JSON snapshot. We deliberately
  // only include `encrypted_keys[user.id]` — the current user's per-message AES
  // key wrapped with their own RSA public key — so the exported file is
  // decryptable on any device this user signs into (where the matching RSA
  // private key exists) but useless to anyone else, including other
  // conversation participants. No plaintext ever touches disk.
  const handleExportHistory = async () => {
    if (!messages.length) {
      toast.info('Nothing to export yet in this conversation.');
      return;
    }
    try {
      const exportedMessages = messages.map((m) => ({
        id: m.id,
        sender_id: resolveSenderId(m) || null,
        encrypted_content: m.encrypted_content,
        // Ship ONLY the current user's wrapped AES key — other recipients'
        // keys are irrelevant for local decryption and privacy-preserving.
        encrypted_key_for_exporter: m.encrypted_keys?.[user.id] || null,
        created_at: m.created_at,
        expires_at: m.expires_at,
        is_file: !!m.is_file,
        reactions: m.reactions || {},
      }));
      // Integrity: SHA-256 over the canonicalised message array. Consumers
      // can recompute and compare to detect any bit-level tampering with
      // the exported file (e.g. someone swapped a ciphertext blob).
      const integrityHash = await computeMessagesHash(exportedMessages);
      const payload = {
        app: 'GhostComm',
        format: 'conversation-export-v2',
        exportedAt: new Date().toISOString(),
        exportedBy: { userId: user.id, username: user.username },
        conversation: {
          id: conversation.id,
          type: conversation.type,
          name: conversationName,
          participants: conversation.participants,
        },
        messageCount: exportedMessages.length,
        integrity: { algorithm: 'SHA-256', hash: integrityHash },
        messages: exportedMessages,
      };
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .slice(0, 19);
      const safeName = conversationName.replace(/[^a-z0-9_-]/gi, '_').slice(0, 40) || 'chat';
      const a = document.createElement('a');
      a.href = url;
      a.download = `ghostcomm-chat-${safeName}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(
        `Exported ${messages.length} encrypted message${messages.length === 1 ? '' : 's'}. ` +
          `Only your RSA private key can decrypt this file.`,
        { duration: 5000 }
      );
    } catch (e) {
      toast.error('Export failed: ' + (e?.message || 'unknown'));
    }
  };

  // ─── Import encrypted history ─────────────────────────────────────
  // Parse + validate a `ghostcomm-chat-*.json` file. We insist:
  //   1. `app === "GhostComm"` and `format === "conversation-export-v1"`
  //   2. The file's `exportedBy.userId` matches the current user — otherwise
  //      their wrapped AES keys are useless here, and importing would just
  //      render a timeline of `[Encrypted for a different device]` rows.
  //   3. Each message has an `encrypted_key_for_exporter` so the existing
  //      decrypt loop can pick it up under `encrypted_keys[user.id]`.
  //
  // Non-fatal mismatches (different conversation id) are surfaced as a warning
  // but don't block import — power users do rename/merge chats from backups.
  const handleImportFilePicked = (file) => {
    setImportParsed(null);
    setImportError(null);
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setImportError('File is too large (>50 MB).');
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setImportError('Could not read the file.');
    reader.onload = async () => {
      try {
        const raw = reader.result;
        const data = JSON.parse(typeof raw === 'string' ? raw : '');
        if (!data || typeof data !== 'object') throw new Error('Not a JSON object');
        if (data.app !== 'GhostComm') throw new Error('Not a Government Shield export file.');
        const SUPPORTED = new Set(['conversation-export-v1', 'conversation-export-v2']);
        if (!SUPPORTED.has(data.format)) {
          throw new Error(`Unsupported format: ${data.format || 'missing'}`);
        }
        if (!Array.isArray(data.messages)) throw new Error('No messages array.');
        if (!data.exportedBy || data.exportedBy.userId !== user.id) {
          throw new Error(
            'This export was created by a different user — only your own exports can be imported here.'
          );
        }
        // v2 files carry an `integrity.hash` (SHA-256 over the canonical message
        // list). Recompute + compare so the user finds out if the file was
        // tampered with on disk before any of its messages touch localStorage.
        let integrityState = 'none'; // 'ok' | 'mismatch' | 'none'
        let integrityExpected = null;
        let integrityActual = null;
        if (data.format === 'conversation-export-v2') {
          integrityExpected = data.integrity?.hash || null;
          if (!integrityExpected) {
            throw new Error('v2 export is missing integrity.hash field.');
          }
          integrityActual = await computeMessagesHash(data.messages);
          integrityState = integrityActual === integrityExpected ? 'ok' : 'mismatch';
        }
        const convMismatch =
          data.conversation?.id && data.conversation.id !== conversation.id;
        setImportParsed({
          ...data,
          _convMismatch: convMismatch,
          _integrityState: integrityState,
          _integrityExpected: integrityExpected,
          _integrityActual: integrityActual,
        });
      } catch (err) {
        setImportError(err?.message || 'Could not parse file.');
      }
    };
    reader.readAsText(file);
  };

  const applyImport = () => {
    if (!importParsed) return;
    if (importParsed._integrityState === 'mismatch') {
      toast.error('Integrity check failed — refusing to import.');
      return;
    }
    try {
      // Reshape each exported message into the shape the live decrypt loop
      // expects: `encrypted_keys[user.id]` set from `encrypted_key_for_exporter`.
      const reshaped = importParsed.messages.map((m) => ({
        id: m.id,
        conversation_id: conversation.id,
        sender_id: m.sender_id,
        encrypted_content: m.encrypted_content,
        encrypted_keys: { [user.id]: m.encrypted_key_for_exporter || null },
        created_at: m.created_at,
        expires_at: m.expires_at,
        is_file: !!m.is_file,
        reactions: m.reactions || {},
        _imported: true,
        _importedFromExportedAt: importParsed.exportedAt || null,
      }));
      saveImportedHistory(conversation.id, reshaped, {
        exportedAt: importParsed.exportedAt,
        exportedByUsername: importParsed.exportedBy?.username || null,
      });
      toast.success(`Imported ${reshaped.length} messages — reloading timeline…`);
      setImportOpen(false);
      resetImportState();
      // Merge back into the live view without a full page reload.
      setMessages((prev) => {
        const serverIds = new Set(prev.filter((m) => !m._imported).map((m) => m.id));
        const onlyNewImports = reshaped.filter((m) => !serverIds.has(m.id));
        const withoutOldImports = prev.filter((m) => !m._imported);
        return [...onlyNewImports, ...withoutOldImports].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
      });
    } catch (e) {
      toast.error('Import failed: ' + (e?.message || 'unknown'));
    }
  };

  const handleClearImported = () => {
    clearImportedHistory(conversation.id);
    setMessages((prev) => prev.filter((m) => !m._imported));
    toast.success('Removed imported history from this conversation');
  };

  return (
    <div className="flex-1 flex flex-col relative">
      {/* Header */}
      <div className="min-h-16 flex items-center justify-between px-6 py-2 pr-28 border-b border-border bg-card/30 backdrop-blur-sm">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-secondary text-slate-300">
              {conversation.type === 'group' ? (
                <Users className="w-5 h-5" />
              ) : (
                conversationName[0]?.toUpperCase()
              )}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 max-w-md">
            <p className="font-medium text-white truncate">{conversationName}</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              {conversation.type !== 'group' && (
                <>
                  <span
                    className={`w-2 h-2 rounded-full ${
                      otherParticipant?.is_online ? 'bg-emerald-500' : 'bg-slate-500'
                    }`}
                  />
                  <span>{otherParticipant?.is_online ? 'Online' : 'Offline'}</span>
                </>
              )}
              <Lock className="w-3 h-3 ml-2" />
              <span>E2E Encrypted</span>
              {Object.keys(tokenMap).length > 0 && (
                <span
                  className="inline-flex items-center gap-1 ml-2 text-emerald-400/80"
                  title="Sealed sender active — server does not persist who sent each message"
                  data-testid="sealed-sender-indicator"
                >
                  <Fingerprint className="w-3 h-3" />
                  <span>Sealed</span>
                </span>
              )}
              {cryptoKeys?.kyberPub && peerHasPq && (
                <span className="relative inline-flex ml-2">
                  <button
                    type="button"
                    onClick={() => setSafetyNumberOpen(true)}
                    className="inline-flex items-center gap-1 text-violet-400/90 hover:text-violet-300 transition-colors cursor-pointer"
                    title="Quantum-resistant: messages wrapped with hybrid RSA + ML-KEM-768 (NIST FIPS 203). Tap to see the ML-DSA-65 fingerprint."
                    data-testid="pq-hybrid-indicator"
                  >
                    <Atom className="w-3 h-3" />
                    <span>Quantum-safe</span>
                  </button>
                  <PqTourCard visible={!!(cryptoKeys?.kyberPub && peerHasPq)} />
                </span>
              )}
            </div>
            {conversation.type !== 'group' && otherParticipant?.id && (
              <SafetyStripe
                peerId={otherParticipant.id}
                peerUsername={otherParticipant.username}
                onClick={() => setKeyHistoryOpen(true)}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isDuressMode && (
            <ConversationTTLPicker
              conversation={{ ...conversation, ttl_hours: ttlHours }}
              onUpdated={(updated) => {
                setTtlHours(Number(updated?.ttl_hours ?? 24));
              }}
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const next = !hoverReveal;
              setHoverReveal(next);
              try { window.localStorage.setItem('gs_hover_reveal', next ? '1' : '0'); } catch {}
              if (!next) setRevealedIds(new Set());
            }}
            aria-pressed={hoverReveal}
            title={hoverReveal ? 'Hover-to-reveal ON — messages blur until you hover them' : 'Enable hover-to-reveal'}
            data-testid="hover-reveal-toggle-header"
            className={hoverReveal ? 'text-emerald-400' : ''}
          >
            {hoverReveal ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setSearchOpen((v) => !v);
              setTimeout(() => searchInputRef.current?.focus(), 50);
            }}
            data-testid="search-messages-btn"
            aria-label="Search messages"
            title="Search messages (/)"
          >
            <Search className="w-5 h-5" />
          </Button>
          {conversation.type !== 'group' && !isDuressMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCall(otherParticipant)}
              data-testid="start-call-btn"
            >
              <Phone className="w-5 h-5" />
            </Button>
          )}
          {conversation.type === 'group' && !isDuressMode && onGroupCall && (
            <TooltipProvider delayDuration={250}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onGroupCall(conversation)}
                    data-testid="start-group-call-btn"
                    aria-label="Start encrypted group call"
                  >
                    <Phone className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-xs text-xs leading-relaxed"
                  data-testid="start-group-call-tooltip"
                >
                  Start an end-to-end encrypted group call. Group co-membership
                  lets you voice-call any other member — no need to separately
                  add them as a contact.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="chat-menu-trigger">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setSafetyNumberOpen(true)}
                data-testid="view-encryption-info"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                View encryption info
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setKeyHistoryOpen(true)}
                data-testid="view-key-history"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Key rotation history
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleExportHistory}
                data-testid="export-chat-history"
              >
                <Download className="w-4 h-4 mr-2" />
                Export encrypted history
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setImportOpen(true)}
                data-testid="import-chat-history"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import encrypted history
              </DropdownMenuItem>
              {importedCount > 0 && (
                <DropdownMenuItem
                  onClick={handleClearImported}
                  className="text-amber-400 focus:text-amber-300"
                  data-testid="clear-imported-history"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Forget imported history ({importedCount})
                </DropdownMenuItem>
              )}
              {legacyCount > 0 && (
                <DropdownMenuItem
                  onClick={toggleHideLegacy}
                  data-testid="toggle-hide-legacy"
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  {hideLegacy ? 'Show' : 'Hide'} legacy messages ({legacyCount})
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Search bar (inline) — "/" opens, Esc closes, Up/Down arrows navigate */}
      {searchOpen && (
        <div
          className="flex items-center gap-2 px-4 py-2 border-b border-border bg-card/60 backdrop-blur-sm"
          data-testid="chat-search-bar"
        >
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentMatchIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.shiftKey) prevMatch();
                else nextMatch();
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                nextMatch();
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                prevMatch();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                closeSearch();
              }
            }}
            placeholder="Search messages (plaintext, this device only)…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-200 placeholder:text-slate-500"
            data-testid="chat-search-input"
          />
          <span
            className="text-xs text-slate-500 font-mono shrink-0"
            data-testid="chat-search-match-count"
          >
            {searchQuery.trim()
              ? searchMatches.length > 0
                ? `${currentMatchIndex + 1} / ${searchMatches.length}`
                : '0 / 0'
              : ''}
          </span>
          <button
            onClick={prevMatch}
            disabled={searchMatches.length === 0}
            className="p-1 rounded hover:bg-secondary/60 disabled:opacity-30 text-slate-400"
            title="Previous match (Shift+Enter / ↑)"
            data-testid="chat-search-prev"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={nextMatch}
            disabled={searchMatches.length === 0}
            className="p-1 rounded hover:bg-secondary/60 disabled:opacity-30 text-slate-400"
            title="Next match (Enter / ↓)"
            data-testid="chat-search-next"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            onClick={closeSearch}
            className="p-1 rounded hover:bg-secondary/60 text-slate-400"
            title="Close (Esc)"
            data-testid="chat-search-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-hidden relative">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <ScrollArea className="h-full relative z-10">
          <div className="p-6 space-y-4">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Loading messages...</div>
            ) : visibleMessages.length === 0 ? (
              hideLegacy && legacyCount > 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="all-legacy-hidden">
                  <EyeOff className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>All messages are legacy and currently hidden</p>
                  <button
                    onClick={toggleHideLegacy}
                    className="mt-3 text-sm text-primary hover:underline"
                    data-testid="show-legacy-btn"
                  >
                    Show {legacyCount} legacy message{legacyCount === 1 ? '' : 's'}
                  </button>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Lock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Start an encrypted conversation</p>
                  <p className="text-sm mt-1">Messages auto-destruct after 24 hours</p>
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs">
                    <button
                      onClick={() => {
                        const next = !hoverReveal;
                        setHoverReveal(next);
                        try { window.localStorage.setItem('gs_hover_reveal', next ? '1' : '0'); } catch {}
                        if (!next) setRevealedIds(new Set());
                      }}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                        hoverReveal ? 'text-emerald-300 bg-emerald-500/10' : 'text-slate-400 hover:text-emerald-300'
                      }`}
                      data-testid="hover-reveal-toggle-empty"
                    >
                      {hoverReveal ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      <span>Hover-to-reveal {hoverReveal ? 'on' : 'off'}</span>
                    </button>
                  </div>
                </div>
              )
            ) : (
              <>
                {importedCount > 0 && (
                  <div
                    className="flex items-center justify-center gap-2 text-xs text-emerald-400/80 pb-2"
                    data-testid="imported-history-banner"
                  >
                    <Archive className="w-3 h-3" />
                    <span>
                      {importedCount} message{importedCount === 1 ? '' : 's'} restored from
                      a local backup
                    </span>
                  </div>
                )}
                {hideLegacy && legacyCount > 0 && (
                  <div
                    className="flex items-center justify-center gap-2 text-xs text-slate-500 pb-2"
                    data-testid="legacy-hidden-banner"
                  >
                    <EyeOff className="w-3 h-3" />
                    <span>
                      {legacyCount} legacy message{legacyCount === 1 ? '' : 's'} hidden
                    </span>
                    <button
                      onClick={toggleHideLegacy}
                      className="text-primary hover:underline"
                      data-testid="show-legacy-inline-btn"
                    >
                      Show
                    </button>
                  </div>
                )}
                {visibleMessages.map((msg) => {
                // System messages render as an inline, centered,
                // muted line. They're ephemeral (local-only),
                // never persisted, never reactable. We branch
                // early so the rest of the bubble logic doesn't
                // try to decrypt them.
                if (msg._system) {
                  return (
                    <div
                      key={msg.id}
                      className="flex justify-center my-2"
                      role="status"
                      aria-live="polite"
                      data-testid={`system-message-${msg.id}`}
                    >
                      <div className="text-[11px] uppercase tracking-[2px] text-slate-500 italic px-3 py-1 rounded-full bg-white/[0.02] border border-white/5">
                        {msg.text}
                      </div>
                    </div>
                  );
                }
                const senderId = resolveSenderId(msg);
                const isSelf = senderId === user.id;
                const sender = contacts.find((c) => c.id === senderId);
                const isFileMessage = msg.is_file;

                const handleReactionChange = (newReactions) => {
                  setMessages((prev) => prev.map((m) =>
                    m.id === msg.id ? { ...m, reactions: newReactions } : m
                  ));
                };

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isSelf ? 'justify-end' : 'justify-start'} animate-fadeIn group outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md ${reactingMessageIds[msg.id] ? 'reaction-pulse' : ''} ${searchOpen && searchMatches[currentMatchIndex]?.id === msg.id ? 'ring-2 ring-amber-400/60 rounded-lg' : ''}`}
                    tabIndex={0}
                    role="article"
                    aria-label={`Message from ${isSelf ? 'you' : (sender?.username || 'someone')}. Press R to react.`}
                    onFocus={() => setFocusedMessageId(msg.id)}
                    onBlur={(e) => {
                      // Only clear focus if we're leaving the bubble entirely (not moving to a child)
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setFocusedMessageId((curr) => (curr === msg.id ? null : curr));
                      }
                    }}
                    data-testid={`message-bubble-${msg.id}`}
                  >
                    <div className={`max-w-[70%] ${isSelf ? 'items-end' : 'items-start'}`}>
                      {!isSelf && conversation.type === 'group' && (
                        <p className="text-xs text-slate-500 mb-1 ml-1">
                          {sender?.username || 'Unknown'}
                        </p>
                      )}
                      <div className={`flex items-center gap-1 ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}>
                        <SecureMessage
                          messageId={msg.id}
                          isSelf={isSelf}
                          isWindowFocused={isWindowFocused}
                          alwaysVisible={
                            editingMessageId === msg.id ||
                            (searchOpen && searchMatches.some((m) => m.id === msg.id))
                          }
                        >
                          {isFileMessage ? (
                            <FileMessage
                              message={msg}
                              encryptedKey={msg.encrypted_keys[user.id]}
                              privateKey={cryptoKeys?.privateKey}
                              isOwn={isSelf}
                            />
                          ) : (
                            <div
                              className={isSelf ? 'message-sent' : 'message-received'}
                              data-testid={`message-${msg.id}`}
                            >
                              {(() => {
                                if (msg.deleted) {
                                  return (
                                    <p
                                      className="text-sm italic text-slate-500 inline-flex items-center gap-1.5"
                                      data-testid={`deleted-${msg.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5 shrink-0" />
                                      This message was deleted
                                    </p>
                                  );
                                }
                                // View-once: spent state. Different copy for
                                // sender ("you sent a view-once, it was read")
                                // vs recipient/any-other-device ("already
                                // viewed"). Server permanently clears the
                                // ciphertext on first GET by a non-sender, so
                                // even a sender who refreshes sees this state
                                // — which is the honest anti-surveillance
                                // outcome: nothing recoverable server-side.
                                if (msg.view_once_consumed) {
                                  return (
                                    <p
                                      className="text-sm italic text-amber-400/80 inline-flex items-center gap-1.5"
                                      data-testid={`view-once-spent-${msg.id}`}
                                    >
                                      <Zap className="w-3.5 h-3.5 shrink-0" />
                                      {senderId === user.id
                                        ? 'Your view-once message · viewed by recipient'
                                        : 'View-once message · already viewed'}
                                    </p>
                                  );
                                }
                                if (editingMessageId === msg.id) {
                                  return (
                                    <div
                                      className="flex items-center gap-1.5"
                                      data-testid={`edit-mode-${msg.id}`}
                                    >
                                      <Input
                                        value={editDraft}
                                        onChange={(e) => setEditDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            saveEdit(msg);
                                          }
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            cancelEdit();
                                          }
                                        }}
                                        autoFocus
                                        className="h-7 text-sm bg-background/60 border-primary/40"
                                        data-testid={`edit-input-${msg.id}`}
                                      />
                                      <button
                                        onClick={() => saveEdit(msg)}
                                        disabled={!editDraft.trim() || savingEdit}
                                        className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400 disabled:opacity-40"
                                        title="Save (Enter)"
                                        data-testid={`edit-save-${msg.id}`}
                                      >
                                        <Check className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={cancelEdit}
                                        className="p-1 rounded hover:bg-red-500/20 text-red-400"
                                        title="Cancel (Esc)"
                                        data-testid={`edit-cancel-${msg.id}`}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  );
                                }
                                const text = decryptedMessages[msg.id];
                                const isUndecryptable = text === '[Encrypted for a different device]';
                                const isGenericFailure = text === '[Decryption failed]';
                                if (isUndecryptable || isGenericFailure) {
                                  const tipBody = isUndecryptable
                                    ? "This message was sent before Government Shield was installed on this device, or to a different device's key. Your other device can still read it. New messages will decrypt normally."
                                    : 'Something went wrong decrypting this message. This is rare — if it happens repeatedly, try signing out and back in.';
                                  return (
                                    <TooltipProvider delayDuration={150}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <p
                                            className="text-sm italic text-slate-400 inline-flex items-center gap-1.5 cursor-help"
                                            data-testid={`undecryptable-${msg.id}`}
                                          >
                                            <Info className="w-3.5 h-3.5 shrink-0" />
                                            {text}
                                          </p>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                                          {tipBody}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                }
                                return (
                                  <p
                                    className={`text-sm break-words transition-all duration-150 ${
                                      hoverReveal && !revealedIds.has(msg.id)
                                        ? 'blur-md select-none cursor-pointer'
                                        : ''
                                    }`}
                                    onMouseEnter={() => {
                                      if (!hoverReveal) return;
                                      setRevealedIds((prev) => {
                                        if (prev.has(msg.id)) return prev;
                                        const n = new Set(prev);
                                        n.add(msg.id);
                                        return n;
                                      });
                                    }}
                                    onMouseLeave={() => {
                                      if (!hoverReveal) return;
                                      setRevealedIds((prev) => {
                                        if (!prev.has(msg.id)) return prev;
                                        const n = new Set(prev);
                                        n.delete(msg.id);
                                        return n;
                                      });
                                    }}
                                    data-testid={`message-body-${msg.id}`}
                                  >
                                    {searchQuery.trim() && text ? highlightedText(text) : (text || 'Decrypting...')}
                                  </p>
                                );
                              })()}
                            </div>
                          )}
                        </SecureMessage>
                        <ReactionPicker
                          isOpen={openReactionPicker === msg.id}
                          openedByKeyboard={pickerOpenedByKeyboard && openReactionPicker === msg.id}
                          onOpenChange={(open) => {
                            setOpenReactionPicker(open ? msg.id : null);
                            if (!open) setPickerOpenedByKeyboard(false);
                            if (open) {
                              sendWsMessage({
                                type: 'reaction_typing',
                                conversation_id: conversation.id,
                                message_id: msg.id,
                              });
                            }
                          }}
                          onSelect={async (emoji) => {
                            // Duress: toggle reaction locally, skip
                            // the POST /reactions call entirely.
                            if (isDuressMode) {
                              const prevReactions = msg.reactions || {};
                              const list = prevReactions[emoji] || [];
                              const mine = list.includes(user.id);
                              const nextList = mine
                                ? list.filter((u) => u !== user.id)
                                : [...list, user.id];
                              const nextReactions = { ...prevReactions };
                              if (nextList.length === 0) {
                                delete nextReactions[emoji];
                              } else {
                                nextReactions[emoji] = nextList;
                              }
                              setMessages((prev) => prev.map((m) =>
                                m.id === msg.id ? { ...m, reactions: nextReactions } : m
                              ));
                              setDuressState((prev) => prev ? {
                                ...prev,
                                messages: (prev.messages || []).map((m) =>
                                  m.id === msg.id ? { ...m, reactions: nextReactions } : m
                                ),
                              } : prev);
                              handleReactionChange(nextReactions);
                              return;
                            }
                            try {
                              const res = await axios.post(`${API}/messages/${msg.id}/reactions`, { emoji });
                              handleReactionChange(res.data.reactions);
                            } catch (e) {
                              toast.error('Failed to add reaction');
                            }
                          }}
                        />
                        {isSelf && !msg.deleted && !msg.is_file && editingMessageId !== msg.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-secondary/70 text-slate-400"
                                data-testid={`message-menu-${msg.id}`}
                                aria-label="Message options"
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isSelf ? 'end' : 'start'}>
                              <DropdownMenuItem
                                disabled={!canEditMessage(msg)}
                                onClick={() => beginEdit(msg)}
                                data-testid={`message-edit-${msg.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                {canEditMessage(msg) ? 'Edit' : 'Edit (window expired)'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!canDeleteMessage(msg)}
                                onClick={() => deleteMessage(msg)}
                                className="text-red-400 focus:text-red-300"
                                data-testid={`message-delete-${msg.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <div className={`${isSelf ? 'flex justify-end' : ''}`}>
                        <MessageReactions
                          messageId={msg.id}
                          reactions={msg.reactions || {}}
                          currentUserId={user.id}
                          onReactionChange={handleReactionChange}
                        />
                      </div>
                      <div
                        className={`flex items-center gap-2 mt-1 text-xs text-slate-500 ${
                          isSelf ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <span>{formatTime(msg.created_at)}</span>
                        <Clock className="w-3 h-3" />
                        <span>{getTimeRemaining(msg.expires_at)}</span>
                        {msg.edited && !msg.deleted && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-flex items-center gap-1 text-slate-400/70 cursor-help"
                                  data-testid={`edited-badge-${msg.id}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                  edited
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                {msg.edited_at
                                  ? `Edited ${new Date(msg.edited_at).toLocaleString()}`
                                  : 'This message was edited'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {msg.view_once && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-flex items-center gap-1 text-amber-400/80 cursor-help"
                                  data-testid={`view-once-badge-${msg.id}`}
                                >
                                  <Zap className="w-3 h-3" />
                                  {msg.view_once_consumed ? 'burned' : 'burn on read'}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs max-w-xs">
                                {msg.view_once_consumed
                                  ? 'The recipient opened this once. The server has cleared its ciphertext.'
                                  : 'This message will self-destruct the moment the recipient opens the conversation.'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {msg._imported && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-flex items-center gap-1 text-emerald-400/70 cursor-help"
                                  data-testid={`imported-badge-${msg.id}`}
                                >
                                  <Archive className="w-3 h-3" />
                                  restored
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                Restored from a local backup you imported
                                {msg._importedFromExportedAt && (
                                  <>
                                    {' '}
                                    (exported{' '}
                                    {new Date(msg._importedFromExportedAt).toLocaleDateString()})
                                  </>
                                )}
                                .
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              </>
            )}
            {typingUsers.length > 0 && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl rounded-bl-sm px-4 py-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-slate-400 typing-dot" />
                    <span className="w-2 h-2 rounded-full bg-slate-400 typing-dot" />
                    <span className="w-2 h-2 rounded-full bg-slate-400 typing-dot" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Peer-cadence Nudge — shown only when the other participant
          is slipping/inactive. Reuses the conversation's existing
          encrypt + send path so the nudge is a normal encrypted
          message (not a side-channel). Hidden in duress mode and
          for group chats via guards inside the component. */}
      {conversation.type !== 'group' && otherParticipant && !isDuressMode && (
        <RotationNudgePrompt
          peer={otherParticipant}
          conversation={conversation}
          getParticipantPublicKeys={getParticipantPublicKeys}
          encryptMessage={encryptMessage}
          onSent={(row, plain) => {
            setMessages((prev) => [...prev, row]);
            setDecryptedMessages((prev) => ({ ...prev, [row.id]: plain }));
            sendWsMessage({
              type: 'message',
              conversation_id: conversation.id,
              message: row,
            });
          }}
        />
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-border bg-card/30 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <VirtualKeyboardTrigger onClick={onOpenVirtualKeyboard} />
          {!isDuressMode && (
            <FileUpload
              conversationId={conversation.id}
              recipientPublicKeys={Object.fromEntries(
                conversation.participants
                  .filter((p) => p !== user.id)
                  .map((pid) => {
                    const contact = contacts.find((c) => c.id === pid);
                    return [pid, contact?.public_key];
                  })
                  .filter(([, pk]) => pk)
              )}
            />
          )}
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            placeholder={viewOnceArmed ? 'Burn-on-read · vanishes after the first read…' : 'Type an encrypted message...'}
            className={`flex-1 bg-secondary/50 border-transparent focus:border-primary ${viewOnceArmed ? 'ring-1 ring-amber-400/50' : ''}`}
            data-testid="message-input"
          />
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewOnceArmed((v) => !v)}
                  className={viewOnceArmed ? 'text-amber-400 bg-amber-500/10' : 'text-slate-400'}
                  aria-label={viewOnceArmed ? 'Burn-on-read ON' : 'Burn-on-read OFF'}
                  aria-pressed={viewOnceArmed}
                  data-testid="view-once-toggle"
                >
                  <Zap className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs max-w-xs">
                {viewOnceArmed
                  ? 'Burn-on-read ARMED · next message self-destructs the moment the recipient opens it. Resets after send.'
                  : 'Burn-on-read · next message ciphertext is wiped server-side on the recipient\'s first GET. Strictly one read, no replay.'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="glow-primary"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
          <Lock className="w-3 h-3" />
          <span>E2E encrypted</span>
          <span className="mx-1">•</span>
          <Keyboard className="w-3 h-3" />
          <span>Secure keyboard</span>
          <span className="mx-1">•</span>
          <span>Files up to 500MB</span>
          <span className="mx-1">•</span>
          <span>
            Press <kbd className="px-1 py-0.5 rounded bg-secondary/70 border border-border text-[10px] font-mono">Tab</kbd>{' '}
            then <kbd className="px-1 py-0.5 rounded bg-secondary/70 border border-border text-[10px] font-mono">R</kbd> to react
          </span>
        </div>
      </form>

      {/* Import encrypted history dialog */}
      <Dialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) resetImportState();
        }}
      >
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-primary" />
              Import encrypted history
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Pick a{' '}
              <code className="text-slate-300 font-mono text-[11px]">
                ghostcomm-chat-*.json
              </code>{' '}
              file you exported earlier. Messages will be stored locally on this device
              and merged into this chat — they stay encrypted at rest and only your RSA
              private key can read them.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <input
              ref={importFileRef}
              type="file"
              accept="application/json,.json"
              onChange={(e) => handleImportFilePicked(e.target.files?.[0])}
              className="block w-full text-xs text-slate-400
                         file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0
                         file:bg-primary/20 file:text-primary file:text-xs
                         file:cursor-pointer hover:file:bg-primary/30"
              data-testid="import-chat-file-input"
            />

            {importError && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
                data-testid="import-chat-error"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{importError}</span>
              </div>
            )}

            {importParsed && (
              <div className="space-y-2" data-testid="import-chat-preview">
                <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p>
                      Valid export · <strong>{importParsed.messages.length}</strong> message
                      {importParsed.messages.length === 1 ? '' : 's'} ready to import.
                    </p>
                    {importParsed.exportedAt && (
                      <p className="text-slate-400">
                        Exported from &ldquo;
                        {importParsed.conversation?.name || 'chat'}&rdquo; on{' '}
                        {new Date(importParsed.exportedAt).toLocaleString()}.
                      </p>
                    )}
                  </div>
                </div>
                {importParsed._convMismatch && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      This export is from a <em>different</em> conversation
                      (&ldquo;{importParsed.conversation?.name || 'unknown'}&rdquo;). The
                      messages will be merged into the current chat anyway.
                    </span>
                  </div>
                )}
                {importParsed._integrityState === 'ok' && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs"
                    data-testid="import-chat-integrity-ok"
                  >
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p>Integrity verified (SHA-256 match).</p>
                      <p className="text-slate-400 font-mono text-[10px] truncate">
                        {importParsed._integrityActual?.slice(0, 40)}…
                      </p>
                    </div>
                  </div>
                )}
                {importParsed._integrityState === 'mismatch' && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
                    data-testid="import-chat-integrity-mismatch"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p>
                        <strong>Integrity check FAILED.</strong> Either the file was modified
                        on disk, or it was created by a buggy tool. Import is blocked.
                      </p>
                      <p className="text-slate-400 font-mono text-[10px] truncate">
                        expected {importParsed._integrityExpected?.slice(0, 12)}… got{' '}
                        {importParsed._integrityActual?.slice(0, 12)}…
                      </p>
                    </div>
                  </div>
                )}
                {importParsed._integrityState === 'none' && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-md bg-slate-500/10 border border-slate-500/30 text-slate-400 text-xs"
                    data-testid="import-chat-integrity-none"
                  >
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      This is an older v1 export without an integrity hash. The file will
                      be imported as-is — tampering cannot be detected automatically. Future
                      exports will include a SHA-256 hash.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setImportOpen(false)}
              data-testid="import-chat-cancel"
            >
              Cancel
            </Button>
            <Button
              disabled={!importParsed || importParsed._integrityState === 'mismatch'}
              onClick={applyImport}
              className="bg-primary hover:bg-primary/90 disabled:opacity-40"
              data-testid="import-chat-confirm"
            >
              <Upload className="w-3 h-3 mr-1" />
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Safety-number (MITM verification) dialog — 1:1 chats only */}
      {conversation.type !== 'group' && (
        <SafetyNumberDialog
          isOpen={safetyNumberOpen}
          onClose={() => setSafetyNumberOpen(false)}
          me={user}
          peer={getOtherParticipant()}
        />
      )}
      {/* Key rotation history — works for 1:1 and groups (shows each party's key history) */}
      {conversation.type !== 'group' && (
        <KeyTransparencyDialog
          isOpen={keyHistoryOpen}
          onClose={() => setKeyHistoryOpen(false)}
          peer={getOtherParticipant()}
          isSelf={false}
        />
      )}
    </div>
  );
};
